import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import Dropdown from '../Dropdown/Dropdown';
import { trackNavLinkClick, trackNavMenuToggle, trackThemeToggle } from '../../analytics/analytics';

const Navbar: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-logo">
          <Link
            to="/"
            aria-label="FinBuffer Home"
            onClick={() => trackNavLinkClick('logo_home')}
          >
            <span className="navbar-title">FinBuffer</span>
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="navbar-menu">
          <Link
            to="/"
            className={`navbar-link ${isActive('/') ? 'active' : ''}`}
            onClick={() => trackNavLinkClick('nav_home')}
          >
            Home
          </Link>
          <Dropdown
            label="Learn"
            items={[
              { label: 'Financial Runway Calculator', to: '/learn/financial-runway' },
              { label: 'Emergency Fund Guide', to: '/learn/emergency-fund' },
              { label: 'Safe EMI Percentage', to: '/learn/safe-emi-percentage' },
              { label: 'Build a 3-Month Buffer', to: '/learn/build-3-month-buffer' },
            ]}
          />
          <Link
            to="/about"
            className={`navbar-link ${isActive('/about') ? 'active' : ''}`}
            onClick={() => trackNavLinkClick('nav_about')}
          >
            About
          </Link>
        </div>

        {/* Right Side */}
        <div className="navbar-right">
          <button
            className="navbar-theme-toggle"
            suppressHydrationWarning
            onClick={() => {
              toggleTheme();
              trackThemeToggle(theme === 'dark' ? 'light' : 'dark');
            }}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            aria-pressed={theme === 'dark'}
          >
            <span aria-hidden="true" suppressHydrationWarning>{theme === 'dark' ? '☀' : '◐'}</span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="navbar-mobile-toggle"
          onClick={() => {
            const next = !isMobileMenuOpen;
            setIsMobileMenuOpen(next);
            trackNavMenuToggle(next);
          }}
          aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
        >
          <span aria-hidden="true">{isMobileMenuOpen ? '×' : '☰'}</span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="navbar-mobile-menu" id="mobile-navigation">
          <Link
            to="/"
            onClick={() => {
              setIsMobileMenuOpen(false);
              trackNavLinkClick('nav_home_mobile');
            }}
          >
            Home
          </Link>
          <div className="navbar-mobile-submenu">
            <span>Learn</span>
            <Link
              to="/learn/financial-runway"
              onClick={() => {
                setIsMobileMenuOpen(false);
                trackNavLinkClick('nav_learn_financial_runway_mobile');
              }}
            >
              Financial Runway Calculator
            </Link>
            <Link
              to="/learn/emergency-fund"
              onClick={() => {
                setIsMobileMenuOpen(false);
                trackNavLinkClick('nav_learn_emergency_fund_mobile');
              }}
            >
              Emergency Fund Guide
            </Link>
            <Link
              to="/learn/safe-emi-percentage"
              onClick={() => {
                setIsMobileMenuOpen(false);
                trackNavLinkClick('nav_learn_safe_emi_mobile');
              }}
            >
              Safe EMI Percentage
            </Link>
            <Link
              to="/learn/build-3-month-buffer"
              onClick={() => {
                setIsMobileMenuOpen(false);
                trackNavLinkClick('nav_learn_buffer_mobile');
              }}
            >
              Build a 3-Month Buffer
            </Link>
          </div>
          <Link
            to="/about"
            onClick={() => {
              setIsMobileMenuOpen(false);
              trackNavLinkClick('nav_about_mobile');
            }}
          >
            About
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;